"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, FormFeedback } from "reactstrap"
import { TurnstileAssignmentService } from "../../../Service/TurnstileService"
import type { TurnstileAssignment, AccountPerson, Turnstile } from "../turnstile/types"
import { toast } from "react-toastify"
import Swal from "sweetalert2"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: TurnstileAssignment | null
}

const TurnstileAssignmentModal: React.FC<Props> = ({ isOpen, toggle, onSuccess, initialData }) => {
  const [form, setForm] = useState<{
    user: number | null
    turnstile: number
    status: "active" | "revoked" | "pending"
  }>({
    user: null,
    turnstile: 0,
    status: "pending",
  })

  const [accounts, setAccounts] = useState<AccountPerson[]>([])
  const [turnstiles, setTurnstiles] = useState<Turnstile[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    if (isOpen) {
      fetchDropdownData()
    }
  }, [isOpen])

  useEffect(() => {
    if (initialData) {
      setForm({
        user: initialData.user,
        turnstile: initialData.turnstile,
        status: initialData.status,
      })
    } else {
      setForm({
        user: null,
        turnstile: 0,
        status: "pending",
      })
    }
    setErrors({})
  }, [initialData, isOpen])

  const fetchDropdownData = async () => {
    try {
      setLoadingData(true)
      const [accountsData, turnstilesData] = await Promise.all([
        TurnstileAssignmentService.getAccountsForAssignment(),
        TurnstileAssignmentService.getAvailableTurnstiles(),
      ])
      setAccounts(accountsData)
      setTurnstiles(turnstilesData)
    } catch (error) {
      console.error("Failed to fetch dropdown data:", error)
      toast.error("Failed to load form data")
    } finally {
      setLoadingData(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setForm({
      ...form,
      [name]: name === "user" || name === "turnstile" ? (value ? Number(value) : null) : value,
    })

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!form.user) {
      newErrors.user = "User is required"
    }

    if (!form.turnstile) {
      newErrors.turnstile = "Turnstile is required"
    }

    if (!form.status) {
      newErrors.status = "Status is required"
    }

    return newErrors
  }

  const handleSubmit = async () => {
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    try {
      setLoading(true)
      if (initialData) {
        await TurnstileAssignmentService.update(initialData.id, form)
        await Swal.fire("Updated!", "Assignment updated successfully.", "success")
      } else {
        await TurnstileAssignmentService.create(form)
        await Swal.fire("Created!", "Assignment created successfully.", "success")
      }
      onSuccess()
      toggle()
    } catch (error: any) {
      console.error("Failed to save assignment:", error)
      if (error.response?.data?.non_field_errors) {
        await Swal.fire("Error", error.response.data.non_field_errors[0], "error")
      } else {
        await Swal.fire("Error", "Something went wrong. Please try again.", "error")
      }
    } finally {
      setLoading(false)
    }
  }

  if (loadingData) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} size="md">
        <ModalHeader toggle={toggle}>{initialData ? "Edit" : "New"} Assignment</ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </ModalBody>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>{initialData ? "Edit" : "New"} Assignment</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="user">User *</Label>
          <Input type="select" name="user" value={form.user || ""} onChange={handleChange} invalid={!!errors.user}>
            <option value="">-- Select User --</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.person_name}
              </option>
            ))}
          </Input>
          <FormFeedback>{errors.user}</FormFeedback>
        </FormGroup>

        <FormGroup>
          <Label for="turnstile">Turnstile *</Label>
          <Input
            type="select"
            name="turnstile"
            value={form.turnstile || ""}
            onChange={handleChange}
            invalid={!!errors.turnstile}
          >
            <option value="">-- Select Turnstile --</option>
            {turnstiles.map((turnstile) => (
              <option key={turnstile.id} value={turnstile.id}>
                {turnstile.number} ({turnstile.status})
              </option>
            ))}
          </Input>
          <FormFeedback>{errors.turnstile}</FormFeedback>
          <small className="text-muted">Only available turnstiles are shown</small>
        </FormGroup>

        <FormGroup>
          <Label for="status">Status *</Label>
          <Input type="select" name="status" value={form.status} onChange={handleChange} invalid={!!errors.status}>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="revoked">Revoked</option>
          </Input>
          <FormFeedback>{errors.status}</FormFeedback>
        </FormGroup>

        <div className="text-end mt-4">
          <Button color="secondary" onClick={toggle} className="me-2">
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <Spinner size="sm" /> : initialData ? "Update" : "Create"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default TurnstileAssignmentModal
